import type {
  CommunityCupDivisionData,
  CommunityCupPair,
  CommunityCupPairAssignment,
  CommunityCupRosterEntry,
  CommunityCupRosterPreparation,
  CommunityCupSidePreparation,
  PlayerProfile,
} from "@/types/tournament";
import { getEffectiveElo } from "@/utils/liveTournament/constants";

export type RosterReadinessReasonCode =
  | "missing-players"
  | "unmatched-player"
  | "incomplete-pairs"
  | "duplicate-player";

export interface RosterReadinessReason {
  code: RosterReadinessReasonCode;
  message: string;
  playerId?: string;
}

export interface SideRosterReadiness {
  ready: boolean;
  completePairCount: number;
  expectedPairCount: number;
  reasons: RosterReadinessReason[];
}

export interface DivisionRosterReadiness {
  ready: boolean;
  sides: Record<"A" | "B", SideRosterReadiness>;
  globalReasons: RosterReadinessReason[];
}

const emptySide = (): CommunityCupSidePreparation => ({ entries: [], pairAssignments: [] });

export const emptyRosterPreparation = (): CommunityCupRosterPreparation => ({
  A: emptySide(),
  B: emptySide(),
});

const copyProfile = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const makeId = (prefix: string): string => {
  const randomId = globalThis.crypto?.randomUUID?.();
  return `${prefix}-${randomId ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
};

const sideFromPairs = (pairs: CommunityCupPair[], side: "A" | "B"): CommunityCupSidePreparation => {
  const entries: CommunityCupRosterEntry[] = [];
  const pairAssignments: CommunityCupPairAssignment[] = [];

  [...pairs]
    .sort((left, right) => left.seed - right.seed)
    .forEach((pair) => {
      const oneId = `${side}-seed-${pair.seed}-one-${pair.playerOne.id}`;
      const twoId = `${side}-seed-${pair.seed}-two-${pair.playerTwo.id}`;
      entries.push(
        {
          entryId: oneId,
          rawName: pair.playerOne.name,
          matchedProfile: copyProfile(pair.playerOne),
          isReserve: false,
        },
        {
          entryId: twoId,
          rawName: pair.playerTwo.name,
          matchedProfile: copyProfile(pair.playerTwo),
          isReserve: false,
        },
      );
      pairAssignments.push({
        assignmentId: `${side}-seed-${pair.seed}`,
        playerOneEntryId: oneId,
        playerTwoEntryId: twoId,
        seed: pair.seed,
      });
    });

  return { entries, pairAssignments };
};

export const bootstrapRosterPreparation = (
  pairsA: CommunityCupPair[],
  pairsB: CommunityCupPair[],
): CommunityCupRosterPreparation => ({
  A: sideFromPairs(pairsA, "A"),
  B: sideFromPairs(pairsB, "B"),
});

const normalizeSide = (value: CommunityCupSidePreparation | null | undefined): CommunityCupSidePreparation => {
  const entries = Array.isArray(value?.entries)
    ? value.entries.filter((entry): entry is CommunityCupRosterEntry => (
      Boolean(entry)
      && typeof entry.entryId === "string"
      && typeof entry.rawName === "string"
      && typeof entry.isReserve === "boolean"
    )).map(copyProfile)
    : [];
  const entryIds = new Set(entries.map((entry) => entry.entryId));
  const pairAssignments = Array.isArray(value?.pairAssignments)
    ? value.pairAssignments.filter((assignment): assignment is CommunityCupPairAssignment => {
      if (!assignment || typeof assignment.assignmentId !== "string") return false;
      const oneValid = assignment.playerOneEntryId == null || entryIds.has(assignment.playerOneEntryId);
      const twoValid = assignment.playerTwoEntryId == null || entryIds.has(assignment.playerTwoEntryId);
      return oneValid && twoValid;
    }).map(copyProfile)
    : [];
  // Deferred-roster is only meaningful while the side is genuinely empty —
  // once any entry exists there's real work to protect, so the flag can't
  // stick around and hide it.
  return { entries, pairAssignments, pending: entries.length === 0 && value?.pending === true };
};

export const normalizeRosterPreparation = (
  value: CommunityCupRosterPreparation | null | undefined,
  legacyPairsA: CommunityCupPair[] = [],
  legacyPairsB: CommunityCupPair[] = [],
): CommunityCupRosterPreparation => {
  if (!value) return bootstrapRosterPreparation(legacyPairsA, legacyPairsB);
  return { A: normalizeSide(value.A), B: normalizeSide(value.B) };
};

/**
 * A roster entry with a typed name but no linked player record is still a
 * real player for scheduling purposes — the roster UI explicitly tells
 * organizers a name alone is fine. The placeholder id is derived from the
 * entry's own (stable, persisted) `entryId` rather than freshly generated
 * per call, since `materializeCommunityCupPairs` runs on every render/save:
 * a random id here would look like "the pair changed" to the server-side
 * scored-seed lock (`community_cup_pair_identity`) on every subsequent save.
 */
const resolveEntryProfile = (entry: CommunityCupRosterEntry): PlayerProfile | null => {
  if (entry.matchedProfile) return entry.matchedProfile;
  const name = entry.rawName.trim();
  if (!name) return null;
  return {
    id: `manual-${entry.entryId}`,
    name,
    alias: name,
    gender: "unspecified",
    isExpat: false,
    startingLevel: "Unspecified",
    currentElo: null,
  };
};

const materializeSidePairs = (side: CommunityCupSidePreparation): CommunityCupPair[] => {
  const entries = new Map(side.entries.map((entry) => [entry.entryId, entry]));
  const usedPlayerIds = new Set<string>();

  return [...side.pairAssignments]
    .sort((left, right) => (left.seed ?? Number.MAX_SAFE_INTEGER) - (right.seed ?? Number.MAX_SAFE_INTEGER))
    .flatMap((assignment, index) => {
      const one = assignment.playerOneEntryId ? entries.get(assignment.playerOneEntryId) : undefined;
      const two = assignment.playerTwoEntryId ? entries.get(assignment.playerTwoEntryId) : undefined;
      const playerOne = one ? resolveEntryProfile(one) : undefined;
      const playerTwo = two ? resolveEntryProfile(two) : undefined;
      if (!one || !two || one.isReserve || two.isReserve || !playerOne || !playerTwo) return [];
      if (playerOne.id === playerTwo.id || usedPlayerIds.has(playerOne.id) || usedPlayerIds.has(playerTwo.id)) return [];
      usedPlayerIds.add(playerOne.id);
      usedPlayerIds.add(playerTwo.id);
      return [{
        seed: assignment.seed ?? index + 1,
        playerOne: copyProfile(playerOne),
        playerTwo: copyProfile(playerTwo),
        aggregateElo: getEffectiveElo(playerOne) + getEffectiveElo(playerTwo),
        manualSeedOverride: false,
      } satisfies CommunityCupPair];
    });
};

export const materializeCommunityCupPairs = (preparation: CommunityCupRosterPreparation): {
  pairsA: CommunityCupPair[];
  pairsB: CommunityCupPair[];
} => ({
  pairsA: materializeSidePairs(preparation.A),
  pairsB: materializeSidePairs(preparation.B),
});

const sideReadiness = (
  side: CommunityCupSidePreparation,
  expectedPairCount: number,
): SideRosterReadiness => {
  const activeEntries = side.entries.filter((entry) => !entry.isReserve);
  const completePairCount = materializeSidePairs(side).length;
  const reasons: RosterReadinessReason[] = [];
  if (activeEntries.length < expectedPairCount * 2) {
    reasons.push({ code: "missing-players", message: `${expectedPairCount * 2 - activeEntries.length} player slots remain` });
  }
  // Informational only: a name-only player (no linked profile) still forms a
  // real pair via `materializeSidePairs`'s placeholder profile, so this must
  // not block readiness — only flag it so organizers know they can still
  // link a real profile later.
  if (activeEntries.some((entry) => !entry.matchedProfile)) {
    reasons.push({ code: "unmatched-player", message: "Some active players aren't linked to a player profile yet" });
  }
  if (completePairCount !== expectedPairCount) {
    reasons.push({ code: "incomplete-pairs", message: `${expectedPairCount - completePairCount} pairs remain` });
  }
  const blockingReasons = reasons.filter((reason) => reason.code !== "unmatched-player");
  return { ready: blockingReasons.length === 0, completePairCount, expectedPairCount, reasons };
};

export const getDivisionRosterReadiness = (
  preparation: CommunityCupRosterPreparation,
  expectedPairCount: number,
): DivisionRosterReadiness => {
  // Name-only entries (no `matchedProfile`) don't share a real player id, so
  // duplicates among them are caught by normalized name instead — otherwise
  // typing "John Smith" on both sides would go undetected.
  const duplicateKey = (entry: CommunityCupRosterEntry): string | null => {
    if (entry.matchedProfile) return `profile:${entry.matchedProfile.id}`;
    const name = entry.rawName.trim().toLowerCase();
    return name ? `name:${name}` : null;
  };
  const seen = new Map<string, { count: number; playerId?: string }>();
  for (const side of [preparation.A, preparation.B]) {
    for (const entry of side.entries) {
      if (entry.isReserve) continue;
      const key = duplicateKey(entry);
      if (!key) continue;
      const existing = seen.get(key);
      seen.set(key, { count: (existing?.count ?? 0) + 1, playerId: entry.matchedProfile?.id });
    }
  }
  const globalReasons = [...seen.values()]
    .filter(({ count }) => count > 1)
    .map(({ playerId }) => ({
      code: "duplicate-player" as const,
      message: "A player can appear only once within a division",
      playerId,
    }));
  const sides = {
    A: sideReadiness(preparation.A, expectedPairCount),
    B: sideReadiness(preparation.B, expectedPairCount),
  };
  return { ready: sides.A.ready && sides.B.ready && globalReasons.length === 0, sides, globalReasons };
};

/**
 * Readiness entry point for persisted division data. Flexible-roster events
 * use the configured pair count. Legacy events had no preparation payload,
 * so their existing pair arrays define the historical playable roster size.
 */
export const getCommunityCupDivisionReadiness = (
  division: Pick<CommunityCupDivisionData, "pairsA" | "pairsB" | "rosterPreparation" | "rosterPreparationVersion">,
  configuredPairCount: number,
): DivisionRosterReadiness => {
  const expectedPairCount = division.rosterPreparationVersion === 1
    ? configuredPairCount
    : Math.max(division.pairsA.length, division.pairsB.length, 1);
  const readiness = getDivisionRosterReadiness(
    normalizeRosterPreparation(division.rosterPreparation, division.pairsA, division.pairsB),
    expectedPairCount,
  );
  // Historical events predate explicit incomplete-roster semantics. They
  // must retain their prior runtime behavior until someone saves them in the
  // new workspace, which writes rosterPreparationVersion = 1 atomically.
  if (division.rosterPreparationVersion !== 1) return { ...readiness, ready: true };
  return readiness;
};

export const copyPairToSidePreparation = (
  source: CommunityCupSidePreparation,
  assignmentId: string,
  target: CommunityCupSidePreparation,
): CommunityCupSidePreparation => {
  const assignment = source.pairAssignments.find((value) => value.assignmentId === assignmentId);
  if (!assignment?.playerOneEntryId || !assignment.playerTwoEntryId) return copyProfile(target);
  const one = source.entries.find((entry) => entry.entryId === assignment.playerOneEntryId);
  const two = source.entries.find((entry) => entry.entryId === assignment.playerTwoEntryId);
  if (!one || !two) return copyProfile(target);

  const oneCopy = { ...copyProfile(one), entryId: makeId("entry") };
  const twoCopy = { ...copyProfile(two), entryId: makeId("entry") };
  return {
    entries: [...copyProfile(target.entries), oneCopy, twoCopy],
    pairAssignments: [...copyProfile(target.pairAssignments), {
      assignmentId: makeId("pair"),
      playerOneEntryId: oneCopy.entryId,
      playerTwoEntryId: twoCopy.entryId,
      seed: assignment.seed,
    }],
  };
};

export const copyEntryToSidePreparation = (
  source: CommunityCupSidePreparation,
  entryId: string,
  target: CommunityCupSidePreparation,
): CommunityCupSidePreparation => {
  const entry = source.entries.find((value) => value.entryId === entryId);
  if (!entry) return copyProfile(target);
  return {
    ...copyProfile(target),
    entries: [...copyProfile(target.entries), { ...copyProfile(entry), entryId: makeId("entry") }],
  };
};

const pairIdentity = (pair: CommunityCupPair | undefined): string => {
  if (!pair) return "";
  return [pair.playerOne.id, pair.playerTwo.id].sort().join(":");
};

export const validateScoredSeedChanges = (
  existingPairs: CommunityCupPair[],
  nextPairs: CommunityCupPair[],
  scoredSeeds: ReadonlySet<number>,
): { valid: boolean; lockedSeeds: number[] } => {
  const existingBySeed = new Map(existingPairs.map((pair) => [pair.seed, pair]));
  const nextBySeed = new Map(nextPairs.map((pair) => [pair.seed, pair]));
  const lockedSeeds = [...scoredSeeds]
    .filter((seed) => pairIdentity(existingBySeed.get(seed)) !== pairIdentity(nextBySeed.get(seed)))
    .sort((left, right) => left - right);
  return { valid: lockedSeeds.length === 0, lockedSeeds };
};
